import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, Grid, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import heroImage from '../assets/hero.jpg';
import Logo from './Logo';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [petals, setPetals] = useState<JSX.Element[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    // Generate sakura petals
    const newPetals = Array.from({ length: 30 }, (_, i) => (
      <div
        key={i}
        className="petal"
        style={{
          left: `${Math.random() * 100}%`,
          width: `${10 + Math.random() * 10}px`,
          height: `${10 + Math.random() * 10}px`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${10 + Math.random() * 10}s`,
        }}
      />
    ));
    setPetals(newPetals);
  }, []);

  return (
    <Box
      className="hero-section"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #2d1b4e 100%)',
        overflow: 'hidden',
      }}
    >
      {/* Animated sakura petals */}
      <Box className="sakura-petals">{petals}</Box>
      
      {/* Animated background orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(233, 30, 99, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'hero-glow 8s ease-in-out infinite',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156, 39, 176, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'hero-glow 10s ease-in-out infinite reverse',
          zIndex: 0,
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              className={`fade-in ${loaded ? 'loaded' : ''}`}
              sx={{ opacity: 0, animation: loaded ? 'fadeIn 0.8s ease-out forwards' : 'none' }}
            >
              {/* Logo and Brand */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Logo size={56} />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: 'white',
                      lineHeight: 1,
                    }}
                  >
                    Sakura
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      letterSpacing: '2px',
                      fontWeight: 500,
                    }}
                  >
                    ShiftSync
                  </Typography>
                </Box>
              </Box>

              {/* Main Headline */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  fontWeight: 800,
                  color: 'white',
                  mb: 3,
                  lineHeight: 1.1,
                  textShadow: '0 0 40px rgba(233, 30, 99, 0.3)',
                }}
              >
                Scheduling Made{' '}
                <Box
                  component="span"
                  className="gradient-text"
                  sx={{
                    display: 'block',
                    background: 'linear-gradient(135deg, #E91E63 0%, #F48FB1 50%, #9C27B0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    backgroundSize: '200% 200%',
                    animation: 'gradient-shift 5s ease infinite',
                  }}
                >
                  Beautiful
                </Box>
              </Typography>
              
              {/* Subtitle */}
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.7,
                  maxWidth: '500px',
                }}
              >
                The intelligent scheduling and shift management platform designed for modern healthcare organizations. 
                Streamline operations, empower your team.
              </Typography>

              {/* Feature Chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                {['AI-Powered', 'Real-time Sync', 'Mobile First', 'HIPAA Compliant'].map((feature, index) => (
                  <Chip
                    key={feature}
                    label={feature}
                    sx={{
                      bgcolor: 'rgba(233, 30, 99, 0.15)',
                      border: '1px solid rgba(233, 30, 99, 0.3)',
                      color: '#F48FB1',
                      fontWeight: 600,
                      animation: `fadeIn 0.6s ease-out ${0.2 + index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  />
                ))}
              </Box>
              
              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  className="sakura-button"
                  sx={{
                    px: 5,
                    py: 1.8,
                    fontSize: '1.1rem',
                    bgcolor: '#E91E63',
                    background: 'linear-gradient(135deg, #E91E63, #C2185B)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #C2185B, #880E4F)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 40px rgba(233, 30, 99, 0.4)',
                    },
                    animation: 'fadeIn 0.8s ease-out 0.4s forwards',
                    opacity: 0,
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/demo')}
                  className="sakura-button"
                  sx={{
                    px: 5,
                    py: 1.8,
                    fontSize: '1.1rem',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.6)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-3px)',
                    },
                    animation: 'fadeIn 0.8s ease-out 0.5s forwards',
                    opacity: 0,
                  }}
                >
                  Watch Demo →
                </Button>
              </Box>

              {/* Trust Indicators */}
              <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 2 }}>
                  TRUSTED BY HEALTHCARE ORGANIZATIONS
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Active Users', value: '10K+' },
                    { label: 'Shifts Managed', value: '500K+' },
                    { label: 'Uptime', value: '99.9%' },
                  ].map((stat) => (
                    <Box key={stat.label}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>
          
          {/* Hero Image */}
          <Grid item xs={12} md={6}>
            <Box
              className="float"
              sx={{
                position: 'relative',
                animation: 'float 6s ease-in-out infinite',
              }}
            >
              {/* Glow effect behind image */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '90%',
                  height: '90%',
                  borderRadius: '24px',
                  background: 'radial-gradient(circle, rgba(233, 30, 99, 0.3) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                  zIndex: 0,
                }}
              />
              
              {/* Main image with glassmorphism frame */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: '24px',
                  overflow: 'hidden',
                  border: '1px solid rgba(233, 30, 99, 0.2)',
                  boxShadow: '0 25px 80px rgba(233, 30, 99, 0.25)',
                  background: 'rgba(26, 26, 46, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <Box
                  component="img"
                  src={heroImage}
                  alt="Sakura ShiftSync Dashboard"
                  sx={{
                    width: '100%',
                    display: 'block',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                />
                
                {/* Overlay gradient */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, transparent 60%, rgba(26, 26, 46, 0.8) 100%)',
                    pointerEvents: 'none',
                  }}
                />
              </Box>

              {/* Floating badges */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  bgcolor: 'rgba(233, 30, 99, 0.9)',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  boxShadow: '0 10px 40px rgba(233, 30, 99, 0.4)',
                  animation: 'float 4s ease-in-out infinite 1s',
                  zIndex: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'white', fontSize: '14px' }}>
                  ✨ AI-Powered
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: '-15px',
                  left: '-15px',
                  bgcolor: 'rgba(156, 39, 176, 0.9)',
                  borderRadius: '12px',
                  padding: '10px 16px',
                  boxShadow: '0 10px 40px rgba(156, 39, 176, 0.4)',
                  animation: 'float 5s ease-in-out infinite 0.5s',
                  zIndex: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '12px' }}>
                  📊 Real-time Analytics
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
